// React
import { useState, useEffect, memo, useMemo } from 'react';

// Custom components
import TemplateItem from 'components/TemplateSubmissionPreview/TemplateItem';

// Styled
import { BodyWrapper, FeedbackEmptyState } from './styled';

// Utils
import { SubmissionItemType } from 'pages/TemplateDashboard/Tabs/Submissions/context';
import { cloneDeep, groupBy } from 'lodash';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Typography from '@mui/material/Typography';
import AccordionDetails from '@mui/material/AccordionDetails';
import Accordion from '@mui/material/Accordion';
import { Chip, Stack, Box } from '@mui/material';
import { isEqual } from 'lodash';
import {
  checkVisibilityWithText,
  generateTemplateHashes,
} from 'components/TaskChecklist/utils';
import FeedbackIcon from 'components/Icons/feedbackIcon';
import useResponsive from 'utils/CustomHooks/useResponsive';
import CheckListHiddenItem from 'components/ChecklistReport/ChecklistLogItem/ChecklistHiddenItem';
import { STEP_TYPES } from 'utils/templates';
import ConnectIcon from 'components/Icons/connectIcon';

interface locationGroupHashInterface {
  [key: string]: string[]; // Each key is a string, and its value is an array of strings
}
const TemplateBody = ({
  logChecklistItems,
  templateLog,
  isPreview = false,
  conditions = null,
  hideCorrectiveTask = false,
  isFeedbackOnlyLogs = false,
  feedbackActions,
  isReviewMode = false,
  canReview = false,
  locationGroupHash = {},
  linearItems = [],
  hideFeedback,
  overwriteHierarchyItem = false,
}: {
  logChecklistItems: SubmissionItemType[];
  templateLog?: any;
  isPreview?: boolean;
  conditions?: any;
  hideCorrectiveTask?: boolean;
  isFeedbackOnlyLogs?: boolean;
  feedbackActions?: any;
  isReviewMode?: boolean;
  canReview?: boolean;
  locationGroupHash?: locationGroupHashInterface;
  linearItems?: SubmissionItemType[];
  hideFeedback?: boolean;
  overwriteHierarchyItem?: boolean;
}) => {
  const [correctiveTasks, setCorrectiveTasks] = useState<any>();
  const { isMobileDeviceMd } = useResponsive();
  const [showFeebackEmptyState, setShowFeebackEmptyState] = useState(true);
  const createCorrectiveTasksObj = (logChecklistItems) => {
    const obj = {};
    logChecklistItems?.forEach((item: any) => {
      obj[item.id] = item.Tasks;
    });
    return obj;
  };

  const handleAddCorrectiveTask = (itemId: string, task: any) => {
    const _task = task?.length ? task[0] : task;
    setCorrectiveTasks((prevState) => ({
      ...prevState,
      [itemId]: [...prevState[itemId], _task],
    }));
  };

  useEffect(() => {
    if (linearItems && linearItems.length) {
      setCorrectiveTasks(createCorrectiveTasksObj(linearItems));
    }
  }, [linearItems]);

  const checkListLogHashes = useMemo(() => {
    return generateTemplateHashes(linearItems, 'ChecklistItemId') || {};
  }, [linearItems]);

  const isAnyItemwithFeedback = (items) => {
    if (!items?.length) return false;

    return items.some((item) => {
      // Check if current item has comments
      const hasComments = item?.Comments?.length > 0;

      // Check if any followup items have comments
      const hasFollowupComments =
        item?.followUps?.length > 0
          ? isAnyItemwithFeedback(item.followUps)
          : false;

      // Check if any subsections have comments
      const hasSubsectionComments =
        item?.subSections?.length > 0
          ? isAnyItemwithFeedback(item.subSections)
          : false;

      // Check if any items in subsections have comments
      const hasSubsectionItemsComments =
        item?.TaskChecklistItems?.length > 0
          ? isAnyItemwithFeedback(item.TaskChecklistItems)
          : false;

      return (
        hasComments ||
        hasFollowupComments ||
        hasSubsectionComments ||
        hasSubsectionItemsComments
      );
    });
  };

  const itemLevelFeedback = (item) => {
    const noFeedback = !(item?.Comments?.length > 0);
    if (!noFeedback && showFeebackEmptyState) setShowFeebackEmptyState(false);

    return noFeedback;
  };

  // Helper to render a section (can be recursive for nested sections)
  const renderSection = (
    originalSection: any,
    level = 0,
    parentSection: any = undefined,
  ) => {
    let section = originalSection;
    if (overwriteHierarchyItem) {
      const linearSection: any = linearItems?.find(
        (item) => item?.id === originalSection?.id,
      );
      section = {
        ...originalSection,
        score: linearSection?.score ?? originalSection?.score,
        progress: linearSection?.progress ?? originalSection?.progress,
      };
    }

    const isSectionHidden = checkVisibilityWithText(
      conditions,
      section,
      checkListLogHashes,
      true,
      locationGroupHash,
    );

    if (isSectionHidden) {
      return (
        <CheckListHiddenItem
          info={isSectionHidden}
          type="Section"
          title={section?.description || section?.title}
        />
      );
    }

    const { progress, score } = section;
    if (
      isFeedbackOnlyLogs &&
      !isAnyItemwithFeedback(section?.TaskChecklistItems) &&
      !isAnyItemwithFeedback(section?.subSections)
    ) {
      return null;
    }

    return (
      <Accordion
        key={section.id}
        sx={{
          background: 'transparent',
          width: '100% !important',
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
        }}
        TransitionProps={{ unmountOnExit: true }}
        defaultExpanded
        disableGutters
        square={false}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            background: '#EBEAFF',
            height: isMobileDeviceMd ? 'auto !important' : '49px !important',
            minHeight: 'initial !important',
            borderRadius: '8px',
          }}
        >
          <Stack
            width="100%"
            direction={isMobileDeviceMd ? 'column' : 'row'}
            alignItems="center"
            justifyContent="space-between"
          >
            {isFeedbackOnlyLogs ? (
              <Typography
                fontWeight={600}
                fontSize={isMobileDeviceMd ? '16px' : '18px'}
                color={'#000000DE'}
              >
                {section.title || section.description}
              </Typography>
            ) : parentSection ? (
              <Stack direction="column" gap="5px">
                {!isMobileDeviceMd && (
                  <Typography className="parentSectionTitle">
                    {parentSection?.title || parentSection?.description}
                  </Typography>
                )}
                <Stack direction="row" gap="3px">
                  {!isMobileDeviceMd && <ConnectIcon className="connectIcon" />}
                  <Typography className="sectionTitle">
                    {section.title || section.description}
                    {(progress?.total || 0) > 0 && (
                      <>
                        {' '}
                        ({progress?.completed || '0'} of{' '}
                        {progress?.total || '0'})
                      </>
                    )}
                  </Typography>
                </Stack>
              </Stack>
            ) : (
              <Typography className="sectionTitle">
                {section.title || section.description}
                {(progress?.total || 0) > 0 && (
                  <>
                    {' '}
                    ({progress?.completed || '0'} of {progress?.total || '0'})
                  </>
                )}
              </Typography>
            )}
            {templateLog?.Checklist?.isScoring && score?.total > 0 && (
              <Chip
                label={`Score: ${score?.percentage}% (${score?.earned} of ${score?.total} pts)`}
                size="small"
                sx={{
                  background: '#CCCBFF',
                  fontWeight: 700,
                  fontSize: '13px',
                  ...(isMobileDeviceMd && {
                    marginTop: '10px',
                  }),
                }}
              />
            )}
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0, pb: 0 }}>
          <Stack spacing={2}>
            {/* Render regular items */}
            {section?.TaskChecklistItems?.map((hierarchyItem: any) => {
              let item = cloneDeep(hierarchyItem);
              if (isReviewMode && linearItems?.length) {
                const linearItem = linearItems?.find(
                  (linearItem) => linearItem.id === item.id,
                );

                item = {
                  ...hierarchyItem,
                  Comments: linearItem?.Comments,
                };
              }
              // Check if item should be hidden
              const isItemHidden = checkVisibilityWithText(
                conditions,
                item,
                checkListLogHashes,
                true,
                locationGroupHash,
              );

              if (isItemHidden) {
                return (
                  <CheckListHiddenItem
                    key={item.id}
                    info={isItemHidden}
                    type="Step"
                    title={item.description}
                  />
                );
              }

              if (isFeedbackOnlyLogs && itemLevelFeedback(item)) {
                return null;
              }

              return (
                <Stack key={item.id} spacing={2}>
                  <TemplateItem
                    onCorrectiveTaskCreationCb={handleAddCorrectiveTask}
                    templateLog={templateLog}
                    item={item}
                    hideCorrectiveTask={hideCorrectiveTask}
                    correctiveTasks={correctiveTasks?.[item.id]}
                    isPreview={isPreview}
                    feedbackActions={feedbackActions}
                    isReviewMode={isReviewMode}
                    canReview={canReview}
                    checkListLogHashes={checkListLogHashes}
                    hideFeedback={hideFeedback}
                  />
                  {/* Render followups if they exist */}
                  {item.followUps && renderFollowupItems(item.followUps)}
                </Stack>
              );
            })}

            {/* Render subsections if they exist */}
            {section?.subSections?.length > 0 && (
              <Stack spacing={2}>
                {section.subSections.map((subsection: any) => (
                  <Box
                    key={subsection.id}
                    sx={{ pl: isMobileDeviceMd ? 0 : 1 }}
                  >
                    {renderSection(subsection, level + 1, section)}
                  </Box>
                ))}
              </Stack>
            )}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  };

  // Helper to render followup items recursively
  const renderFollowupItems = (followupItems: any[]) => {
    if (!followupItems?.length) return null;

    return (
      <Stack spacing={1} sx={{ pl: isMobileDeviceMd ? 0 : 1 }}>
        <Box
          sx={{
            backgroundColor: '#EBEAFF',
            height: '36px',
            borderRadius: '8px',
            padding: '0px 16px',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Typography style={{ fontWeight: 700, fontSize: '15px' }}>
            Followup Questions:
          </Typography>
        </Box>
        {followupItems.map((hierarchyFollowupItem) => {
          let followupItem = cloneDeep(hierarchyFollowupItem);
          if (isReviewMode && linearItems?.length) {
            const linearItem = linearItems?.find(
              (linearItem) => linearItem.id === followupItem.id,
            );

            followupItem = {
              ...hierarchyFollowupItem,
              Comments: linearItem?.Comments,
            };
          }
          const isItemHidden = checkVisibilityWithText(
            conditions,
            followupItem,
            checkListLogHashes,
            true,
            locationGroupHash,
          );

          if (isItemHidden) {
            return (
              <CheckListHiddenItem
                key={followupItem.id}
                info={isItemHidden}
                type="Step"
                title={followupItem.description}
              />
            );
          }

          if (isFeedbackOnlyLogs && itemLevelFeedback(followupItem)) {
            return null;
          }

          return (
            <Stack key={followupItem.id} spacing={2}>
              {followupItem?.type === STEP_TYPES.HEADER ? (
                <Box
                  sx={{
                    backgroundColor: '#EBEAFF',
                    height: '36px',
                    borderRadius: '8px',
                    padding: '0px 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography style={{ fontWeight: 700, fontSize: '15px' }}>
                    {followupItem?.title}
                  </Typography>
                  {templateLog?.Checklist?.isScoring &&
                    followupItem?.score?.total > 0 && (
                      <Chip
                        label={`Score: ${followupItem?.score?.percentage}% (${followupItem?.score?.earned} of ${followupItem?.score?.total} pts)`}
                        size="small"
                        sx={{
                          background: '#CCCBFF',
                          fontWeight: 700,
                          fontSize: '13px',
                        }}
                      />
                    )}
                </Box>
              ) : (
                <TemplateItem
                  onCorrectiveTaskCreationCb={handleAddCorrectiveTask}
                  templateLog={templateLog}
                  item={followupItem}
                  correctiveTasks={correctiveTasks?.[followupItem.id]}
                  isPreview={isPreview}
                  feedbackActions={feedbackActions}
                  isReviewMode={isReviewMode}
                  canReview={canReview}
                  checkListLogHashes={checkListLogHashes}
                  hideCorrectiveTask={hideCorrectiveTask}
                  hideFeedback={hideFeedback}
                />
              )}

              {followupItem.followUps &&
                renderFollowupItems(followupItem.followUps)}
            </Stack>
          );
        })}
      </Stack>
    );
  };

  return (
    <BodyWrapper>
      {isFeedbackOnlyLogs && showFeebackEmptyState && (
        <FeedbackEmptyState>
          <FeedbackIcon className="feedbackIcon" />
          <Typography className="title">No Feedback Yet</Typography>
          <Typography className="description">
            All the steps with feedback will be shown here.
          </Typography>
        </FeedbackEmptyState>
      )}
      {logChecklistItems?.map((hierarchyItem: any) => {
        if (hierarchyItem?.type !== STEP_TYPES.HEADER) {
          let item = cloneDeep(hierarchyItem);
          if (isReviewMode && linearItems?.length) {
            const linearItem = linearItems?.find(
              (linearItem) => linearItem.id === item.id,
            );

            item = {
              ...hierarchyItem,
              Comments: linearItem?.Comments,
            };
          }
          if (
            checkVisibilityWithText(
              conditions,
              item,
              checkListLogHashes,
              true,
              locationGroupHash,
            )
          )
            return null;

          if (isFeedbackOnlyLogs && itemLevelFeedback(item)) return null;

          return (
            <Stack key={hierarchyItem.id} spacing={2}>
              <TemplateItem
                onCorrectiveTaskCreationCb={handleAddCorrectiveTask}
                templateLog={templateLog}
                item={hierarchyItem}
                correctiveTasks={correctiveTasks?.[item.id]}
                isPreview={isPreview}
                feedbackActions={feedbackActions}
                isReviewMode={isReviewMode}
                canReview={canReview}
                hideCorrectiveTask={hideCorrectiveTask}
                checkListLogHashes={checkListLogHashes}
                hideFeedback={hideFeedback}
              />
              {hierarchyItem.followUps &&
                renderFollowupItems(hierarchyItem.followUps)}
            </Stack>
          );
        } else {
          // Render sections using the new helper function
          return renderSection(hierarchyItem);
        }
      })}
    </BodyWrapper>
  );
};

export default memo(TemplateBody, (prevProps, nextProps) => {
  return isEqual(prevProps, nextProps);
});
